import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { BRAND } from '@saesak/shared';
import { Button, Card, Field, Input } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate } from '@/lib/format';
import type { BillingVerifyResult } from '@/types';

const TYPE_LABELS = { QUOTE: '견적서', STATEMENT: '거래명세서' } as const;

/**
 * 정산 문서 진위 확인 (§5.21) — 로그인 없이 접근하는 공개 페이지.
 * PDF 하단에 인쇄된 문서번호·검증코드를 입력하면 발행 사실을 대조해 준다.
 */
export function VerifyPage() {
  const [docNo, setDocNo] = useState('');
  const [code, setCode] = useState('');
  const [result, setResult] = useState<BillingVerifyResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await apiData<BillingVerifyResult>(
        `/verify/billing?docNo=${encodeURIComponent(docNo.trim())}&code=${encodeURIComponent(code.trim())}`,
      );
      setResult(data);
    } catch {
      setError('확인 중 문제가 생겼어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-screen-sm px-6 py-10">
      <div className="mb-8 flex flex-col items-center gap-2 text-center">
        <img src="/brand-icon.svg" alt="" width={56} height={56} />
        <h1 className="text-2xl font-bold">문서 진위 확인</h1>
        <p className="text-base text-ink-sub">
          {BRAND.name}가 발행한 견적서·거래명세서가 맞는지 확인해 드려요.
          <br />
          문서 하단에 인쇄된 번호와 검증코드를 입력해 주세요.
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Field label="문서번호" required>
            <Input
              placeholder="예: S-2026-0001"
              value={docNo}
              onChange={(e) => setDocNo(e.target.value)}
              required
            />
          </Field>
          <Field label="검증코드" required>
            <Input
              placeholder="예: K7QX-P2M9"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </Field>
          <Button type="submit" block loading={loading}>
            확인하기
          </Button>
        </form>
      </Card>

      {error && (
        <p role="alert" className="mt-4 rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
          {error}
        </p>
      )}

      {result && result.valid && result.document && (
        <Card className="mt-4 bg-primary-soft">
          <p className="font-bold text-primary-strong">정상 발행된 문서예요</p>
          <dl className="mt-2 flex flex-col gap-1 text-base">
            <Row label="문서 종류" value={TYPE_LABELS[result.document.type]} />
            <Row label="문서번호" value={result.document.docNo} />
            <Row label="발행일" value={formatDate(result.document.issuedOn)} />
            <Row label="공급받는 자" value={result.document.facilityName} />
            <Row label="금액" value={`${result.document.amount.toLocaleString('ko-KR')}원`} />
            <Row label="직인" value={result.document.sealApplied ? '합성됨' : '없음'} />
          </dl>
        </Card>
      )}

      {result && !result.valid && (
        <Card className="mt-4 bg-accent-soft">
          <p className="font-bold">일치하는 문서를 찾지 못했어요</p>
          <p className="mt-1 text-sm text-ink-sub">
            문서번호와 검증코드를 다시 확인해 주세요. 입력이 정확한데도 확인되지 않으면 발행처에
            문의해 주세요 — 위·변조된 문서일 수 있어요.
          </p>
        </Card>
      )}

      <p className="mt-10 text-center text-sm text-ink-sub">
        <Link to="/" className="underline">
          {BRAND.name} 홈으로
        </Link>
      </p>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="shrink-0 text-ink-sub">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
